if (!customElements.get('superfood-ingredients-carousel')) {
  class SuperfoodIngredientsCarousel extends HTMLElement {
    static positions = new Map();

    connectedCallback() {
      this.track = this.querySelector('[data-ingredients-track]');
      this.previousButton = this.closest('.superfood-ingredients')?.querySelector('[data-ingredients-previous]');
      this.nextButton = this.closest('.superfood-ingredients')?.querySelector('[data-ingredients-next]');
      this.dots = [...this.querySelectorAll('[data-ingredients-dot]')];
      this.storageKey = `superfood-ingredients:${this.dataset.sectionId}`;
      this.currentKey = this.readPosition();
      this.frame = null;
      this.navigationFrame = null;
      this.targetKey = null;
      this.targetLeft = null;
      this.dragPointerId = null;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.hasDragged = false;

      this.onPrevious = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onWheel = this.handleWheel.bind(this);
      this.onClick = this.handleClick.bind(this);

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      this.track?.addEventListener('pointerdown', this.onPointerDown);
      this.track?.addEventListener('pointermove', this.onPointerMove);
      this.track?.addEventListener('pointerup', this.onPointerUp);
      this.track?.addEventListener('pointercancel', this.onPointerUp);
      this.track?.addEventListener('wheel', this.onWheel, { passive: false });
      this.track?.addEventListener('click', this.onClick, true);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goToKey(dot.dataset.ingredientsDot, true)));

      requestAnimationFrame(() => this.goToKey(this.currentKey, false));
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      this.track?.removeEventListener('pointermove', this.onPointerMove);
      this.track?.removeEventListener('pointerup', this.onPointerUp);
      this.track?.removeEventListener('pointercancel', this.onPointerUp);
      this.track?.removeEventListener('wheel', this.onWheel);
      this.track?.removeEventListener('click', this.onClick, true);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.navigationFrame) cancelAnimationFrame(this.navigationFrame);
    }

    get slides() {
      return [...this.querySelectorAll('[data-ingredients-slide]')].filter((slide) => slide.offsetParent !== null);
    }

    readPosition() {
      const key = SuperfoodIngredientsCarousel.positions.get(this.storageKey);
      return key || this.dots[0]?.dataset.ingredientsDot;
    }

    rememberPosition(key) {
      this.currentKey = key;
      SuperfoodIngredientsCarousel.positions.set(this.storageKey, key);
    }

    indexForKey(key) {
      const slides = this.slides;
      const index = slides.findIndex((slide) => slide.dataset.slideKey === key);
      return index >= 0 ? index : 0;
    }

    slideTargetLeft(slide, index, slides = this.slides) {
      if (!this.track || !slide || !slides.length) return 0;

      const maximumScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const firstSlideOffset = slides[0].offsetLeft;
      if (index === slides.length - 1) return maximumScroll;

      return Math.min(Math.max(slide.offsetLeft - firstSlideOffset, 0), maximumScroll);
    }

    goToKey(key, smooth) {
      const slides = this.slides;
      if (!this.track || !slides.length) return;
      const index = this.indexForKey(key);
      const slide = slides[index];
      const targetLeft = this.slideTargetLeft(slide, index, slides);
      this.targetKey = smooth ? slide.dataset.slideKey : null;
      this.targetLeft = smooth ? targetLeft : null;
      this.toggleAttribute('data-is-navigating', smooth);
      this.rememberPosition(slide.dataset.slideKey);
      if (smooth) {
        this.animateTo(targetLeft);
      } else {
        this.cancelNavigation();
        this.track.scrollLeft = targetLeft;
      }
      this.updateState(index, slides, targetLeft);
    }

    step(direction) {
      const slides = this.slides;
      if (!this.track || !slides.length) return;
      const maximumScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const stride = slides.length > 1
        ? Math.abs(slides[1].offsetLeft - slides[0].offsetLeft)
        : this.track.clientWidth;
      const startingLeft = this.targetLeft ?? this.track.scrollLeft;
      const targetLeft = Math.min(Math.max(startingLeft + (direction * stride), 0), maximumScroll);

      this.targetKey = 'arrow';
      this.targetLeft = targetLeft;
      this.setAttribute('data-is-navigating', '');
      this.animateTo(targetLeft);
      this.updateArrowState(targetLeft);
    }

    animateTo(targetLeft) {
      if (!this.track) return;

      this.cancelNavigation(false);

      const startLeft = this.track.scrollLeft;
      const distance = targetLeft - startLeft;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reducedMotion || Math.abs(distance) < 1) {
        this.track.scrollLeft = targetLeft;
        this.handleScrollEnd();
        return;
      }

      const duration = 420;
      const startTime = performance.now();
      const tick = (time) => {
        const progress = Math.min((time - startTime) / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        this.track.scrollLeft = startLeft + (distance * easedProgress);

        if (progress < 1) {
          this.navigationFrame = requestAnimationFrame(tick);
          return;
        }

        this.navigationFrame = null;
        this.track.scrollLeft = targetLeft;
        this.handleScrollEnd();
      };

      this.navigationFrame = requestAnimationFrame(tick);
    }

    cancelNavigation(clearTarget = true) {
      if (this.navigationFrame) cancelAnimationFrame(this.navigationFrame);
      this.navigationFrame = null;

      if (clearTarget) {
        this.targetKey = null;
        this.targetLeft = null;
        this.removeAttribute('data-is-navigating');
      }
    }

    handleScroll() {
      if (this.targetKey || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const slides = this.slides;
        if (!slides.length) return;
        const maximumScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
        const progress = maximumScroll > 0 ? this.track.scrollLeft / maximumScroll : 0;
        const nearestIndex = Math.min(Math.round(progress * (slides.length - 1)), slides.length - 1);
        this.rememberPosition(slides[nearestIndex].dataset.slideKey);
        this.updateState(nearestIndex, slides, this.track.scrollLeft);
      });
    }

    handleScrollEnd() {
      this.cancelNavigation(false);
      this.targetKey = null;
      this.targetLeft = null;
      this.removeAttribute('data-is-navigating');
      this.handleScroll();
    }

    handlePointerDown(event) {
      if (!this.track || event.button !== 0 || this.dragPointerId !== null) return;

      this.dragPointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.track.scrollLeft;
      this.hasDragged = false;
      this.cancelNavigation();
      this.setAttribute('data-is-dragging', '');
      this.track.setPointerCapture(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.track || event.pointerId !== this.dragPointerId) return;

      const distance = event.clientX - this.dragStartX;
      if (Math.abs(distance) > 5) this.hasDragged = true;
      if (!this.hasDragged) return;

      event.preventDefault();
      this.track.scrollLeft = this.dragStartScrollLeft - distance;
    }

    handlePointerUp(event) {
      if (!this.track || event.pointerId !== this.dragPointerId) return;

      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      this.dragPointerId = null;
      this.removeAttribute('data-is-dragging');

      if (this.hasDragged) this.snapToNearestSlide();
    }

    handleWheel(event) {
      const hasHorizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
      if (hasHorizontalIntent) event.preventDefault();
    }

    handleClick(event) {
      if (!this.hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
      this.hasDragged = false;
    }

    snapToNearestSlide() {
      const slides = this.slides;
      if (!this.track || !slides.length) return;

      const nearestSlide = slides.reduce((nearest, slide, index) => {
        const nearestIndex = slides.indexOf(nearest);
        const distance = Math.abs(this.slideTargetLeft(slide, index, slides) - this.track.scrollLeft);
        const nearestDistance = Math.abs(this.slideTargetLeft(nearest, nearestIndex, slides) - this.track.scrollLeft);
        return distance < nearestDistance ? slide : nearest;
      });

      this.goToKey(nearestSlide.dataset.slideKey, true);
    }

    handleResize() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.goToKey(this.currentKey, false);
      });
    }

    handleBlockSelect(event) {
      const selected = event.target.closest('[data-slide-key]');
      if (selected && this.contains(selected)) this.goToKey(selected.dataset.slideKey, true);
    }

    updateArrowState(scrollLeft) {
      const maximumScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      this.previousButton?.toggleAttribute('disabled', scrollLeft <= 2);
      this.nextButton?.toggleAttribute('disabled', maximumScroll - scrollLeft <= 2);
    }

    updateState(index, slides, scrollLeft = this.track.scrollLeft) {
      const key = slides[index]?.dataset.slideKey;
      this.updateArrowState(scrollLeft);
      this.dots.forEach((dot) => dot.setAttribute('aria-current', String(dot.dataset.ingredientsDot === key)));
    }
  }

  customElements.define('superfood-ingredients-carousel', SuperfoodIngredientsCarousel);
}
